import React, { useState } from "react";
import { motion } from "framer-motion";
import { Crown, Shield, Flame } from "lucide-react";
import { GOLD, CRIMSON } from "./AnalysisStation";
import { Readout } from "./ProScoutCharts";

// Brushed metallic gold with a diagonal sheen.
const METAL_GOLD = {
  background: [
    "linear-gradient(115deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.28) 45%, rgba(255,255,255,0) 60%)",
    "repeating-linear-gradient(90deg, rgba(0,0,0,0.05) 0px, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 3px)",
    "linear-gradient(160deg, #6b4d0c 0%, #c9a227 16%, #f7e7a1 30%, #d4af37 44%, #8a6914 58%, #e6c85a 74%, #b8912e 88%, #6b4d0c 100%)",
  ].join(","),
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.5), 0 24px 60px rgba(0,0,0,0.7), 0 0 30px rgba(212,175,55,0.25)",
};
const TIER = { elite: "Elite", academy: "Technical Track", ebook: "Blueprint" };
const initials = (n) => (n || "").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "N";

const Crest = ({ src, alt, testId }) => {
  const [ok, setOk] = useState(true);
  if (!src || !ok) return null;
  return <img data-testid={testId} src={src} alt={alt} onError={() => setOk(false)} className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-contain border border-black/40 bg-white/90 p-0.5" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.6)" }} />;
};

const Stat = ({ k, v }) => (
  <div className="text-center">
    <div className="font-mono text-base sm:text-lg font-bold leading-none" style={{ color: "#1a1204" }}>{v}</div>
    <div className="font-mono text-[7px] tracking-[0.25em] uppercase" style={{ color: "#3d2c08" }}>{k}</div>
  </div>
);

const Ring = ({ value }) => {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" className="flex-shrink-0">
      <defs><linearGradient id="nxRing" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stopColor={CRIMSON} /><stop offset="100%" stopColor={GOLD} /></linearGradient></defs>
      <circle cx="44" cy="44" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
      <motion.circle cx="44" cy="44" r={r} fill="none" stroke="url(#nxRing)" strokeWidth="6" strokeLinecap="round" transform="rotate(-90 44 44)"
        style={{ filter: `drop-shadow(0 0 3px ${GOLD}) drop-shadow(0 0 10px ${GOLD}99) drop-shadow(0 0 22px ${GOLD}55)` }} strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c * (1 - value / 10) }} transition={{ duration: 1.2, ease: "easeOut" }} />
      <text x="44" y="42" textAnchor="middle" fill={GOLD} fontFamily="Roboto Mono, monospace" fontSize="18" fontWeight="700" style={{ filter: `drop-shadow(0 0 6px ${GOLD}aa)` }}>{value.toFixed(1)}</text>
      <text x="44" y="56" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontFamily="Roboto Mono, monospace" fontSize="7" letterSpacing="2">ENGINE</text>
    </svg>
  );
};

// FIFA-style Elite Technical Card + detailed Engine scores (side-by-side on desktop, stacked on mobile).
export const ElitePlayerCard = ({ player, engine: e, drill, api, animated, cardOnly = false, sponsor = false }) => {
  const composite = (e.balance + e.execution + e.pace) / 3;
  const orank = player?.orank;
  const name = player?.display_name || player?.name || "Next O Player";
  const tier = TIER[player?.tier] || "Player";
  const school = player?.school_name || "";
  const club = player?.club_name || "";
  const glint = animated ?? player?.tier === "elite";
  return (
    <div data-testid="elite-player-card" className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-stretch">
      <motion.div className={`relative mx-auto sm:mx-0 w-[220px] flex-shrink-0 rounded-[18px] p-[3px] ${glint ? "elite-card-halo" : ""}`} style={METAL_GOLD} data-testid={glint ? "elite-card-animated" : undefined}
        initial={{ opacity: 0, y: 14, rotateY: -12 }} animate={{ opacity: 1, y: 0, rotateY: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
        <div className={`relative rounded-[15px] overflow-hidden h-full ${glint ? "card-glint" : ""}`} style={{ aspectRatio: "5 / 7", background: "linear-gradient(170deg, #f1d977 0%, #d4af37 35%, #a67c1a 70%, #6b4d0c 100%)" }}>
          {glint && (
            <>
              <span className="card-sparkle" style={{ top: "14%", left: "58%", animationDelay: "0.4s" }} />
              <span className="card-sparkle" style={{ top: "44%", left: "18%", animationDelay: "1.6s", width: 8, height: 8 }} />
              <span className="card-sparkle" style={{ top: "70%", left: "78%", animationDelay: "2.5s" }} />
            </>
          )}
          <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #fff 0, transparent 40%), repeating-linear-gradient(135deg, rgba(0,0,0,0.06) 0 2px, transparent 2px 6px)" }} />
          <div className="absolute inset-x-3 top-3 flex items-start justify-between">
            <div className="leading-none">
              <div data-testid="card-rating" className="font-cover text-5xl" style={{ color: "#1a1204", textShadow: "0 1px 0 rgba(255,255,255,0.35)" }}>{orank ? orank.orank : Math.round(composite * 10)}</div>
              {orank
                ? <div data-testid="card-orank-label" className="font-mono text-[8px] tracking-[0.3em] uppercase font-bold" style={{ color: "#7a5a10", textShadow: "0 0 6px rgba(255,220,120,0.6)" }}>O-Rank</div>
                : <div className="font-mono text-[8px] tracking-[0.3em] uppercase font-bold flex items-center gap-1" style={{ color: "#3d2c08" }}>{player?.tier === "elite" && <Crown size={9} />}{tier}</div>}
              {orank?.bonus > 0 && <div data-testid="card-streak-bonus" className="mt-1 inline-flex items-center gap-0.5 font-mono text-[7px] font-bold uppercase tracking-[0.15em] rounded-sm px-1 py-0.5" style={{ color: "#f5e3a0", background: "#1a1204" }}><Flame size={7} /> Streak +{orank.bonus}</div>}
            </div>
            <div className="flex items-center gap-1.5">
              {player?.school_id && <Crest src={`${api}/school/${player.school_id}/mascot`} alt={school} testId="card-school-crest" />}
              <img data-testid="card-nexto-crest" src="/nexto-logo.png" alt="Next O Soccer" className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-contain bg-black/85 border border-black/40 p-0.5" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.6)" }} />
            </div>
          </div>
          <div className="absolute inset-x-0 top-[26%] bottom-[34%] flex items-center justify-center">
            <div className="relative">
              <div className="absolute -inset-3 rounded-full" style={{ background: "radial-gradient(circle, rgba(196,30,36,0.45), transparent 70%)" }} />
              <div className="relative w-24 h-24 rounded-full flex items-center justify-center font-cover text-4xl text-[#F5C518]" style={{ background: "radial-gradient(circle at 35% 30%, #2a2a2a, #050505)", border: "3px solid #1a1204", boxShadow: "0 10px 30px rgba(0,0,0,0.55)" }}>
                {player?.jersey_number ? `#${player.jersey_number}` : initials(name)}
              </div>
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 pt-3 pb-3 px-3" style={{ background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.18))" }}>
            <div data-testid="card-name" className="font-headings text-lg uppercase tracking-wide leading-none truncate text-center" style={{ color: "#1a1204" }}>{name}</div>
            <div data-testid="card-club" className="font-mono text-[7px] tracking-[0.2em] uppercase text-center mt-1 truncate flex items-center justify-center gap-1" style={{ color: "#3d2c08" }}>
              <Shield size={8} />{[club, school].filter(Boolean).join(" · ") || "Next O Soccer"}
            </div>
            <div className="h-px my-2" style={{ background: "linear-gradient(90deg, transparent, #1a1204, transparent)" }} />
            <div className={`grid ${orank?.ratings ? "grid-cols-4" : "grid-cols-3"}`}>
              {orank?.ratings
                ? <><Stat k="DRI" v={orank.ratings.dribbling?.value} /><Stat k="CTL" v={orank.ratings.ball_control?.value ?? orank.ratings.passing?.value} /><Stat k="SHO" v={orank.ratings.shooting?.value} /><Stat k="MAS" v={orank.ratings.ball_mastery?.value} /></>
                : <><Stat k="BAL" v={e.balance} /><Stat k="EXE" v={e.execution} /><Stat k="PACE" v={e.pace} /></>}
            </div>
            {sponsor && (
              <div data-testid="card-sponsor-slot" className="flex items-center justify-end gap-1.5 mt-2">
                <span className="font-mono text-[6px] tracking-[0.2em] uppercase" style={{ color: "#3d2c08" }}>Partner</span>
                <span className="flex items-center justify-center rounded-[3px] border border-dashed font-mono text-[6px] font-bold tracking-[0.15em] uppercase px-1.5" style={{ height: 14, borderColor: "#1a1204", color: "#1a1204", background: "rgba(255,255,255,0.35)" }}>Your Logo Here</span>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {!cardOnly && <div className="flex-1 min-w-0 rounded-2xl border border-white/10 p-4 flex flex-col justify-between gap-3"
        style={{ background: "linear-gradient(160deg, rgba(34,34,38,0.7), rgba(14,14,16,0.85))", backdropFilter: "blur(14px)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 0 30px rgba(196,30,36,0.18)" }}>
        <div className="flex items-center justify-between">
          <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#F5C518]">Elite_Technical_Card</span>
          <span className="font-mono text-[8px] tracking-[0.2em] uppercase text-white/45 truncate max-w-[50%]">{drill || "Technical Scan"}</span>
        </div>
        <div className="flex items-center gap-4">
          <Ring value={composite} />
          <div className="flex-1 space-y-2.5 min-w-0">
            <Readout k="Balance" v={e.balance} /><Readout k="Execution" v={e.execution} /><Readout k="Game Pace" v={e.pace} />
          </div>
        </div>
        <div className="flex items-center justify-between font-mono text-[8px] tracking-[0.2em] uppercase">
          <span className="text-white/40">Next O Standard · 9.0</span>
          <span data-testid="card-gap" style={{ color: composite >= 9 ? GOLD : CRIMSON }}>{composite >= 9 ? "Standard Met" : `${(9 - composite).toFixed(1)} to Elite`}</span>
        </div>
      </div>}
    </div>
  );
};
